import {
  Component, ElementRef, forwardRef, input, output, signal, ViewChild,
  ContentChild, ChangeDetectionStrategy, booleanAttribute, numberAttribute, inject, OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayModule } from '@angular/cdk/overlay';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AdminuiSelectOptionDirective } from '../adminui-select/adminui-select-option.directive';
import { AdminuiAutocompleteGroupDirective } from './adminui-autocomplete-group.directive';
import { nextAdminuiId } from '../adminui-unique-id';
import { AdminuiTruncationTitleDirective } from '../adminui-truncation-title.directive';
import { I18NService } from '../../../core/i18n/i18n.service';

/**
 * Emitted by `(completeMethod)` when the user types or opens the dropdown. Shape-compatible with
 * PrimeNG's `AutoCompleteCompleteEvent` (same name) so the app's handlers migrate unchanged, while
 * the type now lives with the in-house component instead of `primeng/autocomplete`.
 */
export interface AutoCompleteCompleteEvent { query: string; originalEvent: Event; }
/** Emitted by `(selected)`. Shape-compatible with PrimeNG's `AutoCompleteSelectEvent` (same name). */
export interface AutoCompleteSelectEvent { originalEvent: Event; value: any; }

/**
 * In-house autocomplete (type-in-field) styled with gaia-styles + built on CDK Overlay.
 *
 * API mirrors PrimeNG `p-autoComplete` so migration is a tag swap — the app's server-side search
 * handlers keep working unchanged:
 * - `[suggestions]` — results the parent sets (we do NOT filter; server-side).
 * - `(completeMethod)` emits `{ query, originalEvent }` (typing, or dropdown click where
 *   `originalEvent.type === 'click'`).
 * - `(selected)` emits `{ value }`; `(blurred)` / `(focused)` mirror PrimeNG.
 * - `[dropdown]` toggle button, `[minQueryLength]`, `optionLabel`, `[group]` + group template,
 *   custom item `<ng-template adminuiSelectOption>`.
 *
 * The selected value's label always shows in the input, independent of the current `suggestions`
 * (server-side lists churn). Selecting keeps focus (option mousedown is prevented) so blur does
 * not fire on pick.
 */
@Component({
  selector: 'adminui-autocomplete',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, OverlayModule, AdminuiTruncationTitleDirective],
  templateUrl: './adminui-autocomplete.component.html',
  styleUrl: './adminui-autocomplete.component.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AdminuiAutocompleteComponent), multi: true }
  ],
  host: {
    'class': 'adminui-autocomplete',
    '[class.adminui-autocomplete--open]': 'open()',
    '[class.adminui-autocomplete--disabled]': 'disabled()',
    '[class.adminui-autocomplete--invalid]': 'invalid()'
  }
})
export class AdminuiAutocompleteComponent implements ControlValueAccessor, OnDestroy {
  readonly suggestions = input<any[]>([]);
  readonly optionLabel = input('');
  readonly placeholder = input('');
  readonly minQueryLength = input(1);
  /**
   * Debounce (ms) between the last keystroke and `(completeMethod)` firing, so typing does not hit
   * the server on every key. `0` emits synchronously (no debounce). The dropdown-toggle click
   * always emits immediately — it is a deliberate action, not typing.
   * Default 500 (higher than PrimeNG's 300) tolerates normal, deliberate typing pauses.
   */
  readonly delay = input(500, { transform: numberAttribute });
  readonly emptyMessage = input('');
  readonly dropdown = input(false, { transform: booleanAttribute });
  readonly group = input(false, { transform: booleanAttribute });
  readonly invalid = input(false, { transform: booleanAttribute });
  /** Accessible name for the combobox input + listbox (use when there is no associated visible label). */
  readonly ariaLabel = input('', { alias: 'aria-label' });
  /** Id(s) of visible element(s) that label this control (WAI-ARIA). */
  readonly ariaLabelledby = input('', { alias: 'aria-labelledby' });

  // Event shapes mirror PrimeNG's AutoCompleteCompleteEvent / AutoCompleteSelectEvent so the
  // app's existing handlers (typed against those) migrate with no changes.
  readonly completeMethod = output<AutoCompleteCompleteEvent>();
  readonly selected = output<AutoCompleteSelectEvent>();
  readonly blurred = output<Event>();
  readonly focused = output<Event>();

  @ViewChild('input', { static: true }) inputRef!: ElementRef<HTMLInputElement>;
  @ContentChild(AdminuiSelectOptionDirective) itemTpl?: AdminuiSelectOptionDirective;
  @ContentChild(AdminuiAutocompleteGroupDirective) groupTpl?: AdminuiAutocompleteGroupDirective;

  readonly open = signal(false);
  readonly disabled = signal(false);
  readonly value = signal<any>(null);
  readonly inputText = signal('');
  readonly activeIndex = signal(-1);

  /** Stable ids wiring the WAI-ARIA combobox → listbox → active-option relationships. */
  readonly listboxId = nextAdminuiId('adminui-autocomplete-listbox');
  optionId(index: number): string { return `${this.listboxId}-option-${index}`; }
  /** Id of the active option, exposed via `aria-activedescendant` (null when closed / none). */
  activeDescendant(): string | null {
    return this.open() && this.activeIndex() >= 0 ? this.optionId(this.activeIndex()) : null;
  }

  // Localized accessible name for the dropdown toggle button (reuse I18NService; no duplicate data).
  private readonly i18n = inject(I18NService);
  get toggleLabel(): string { return this.i18n.messages['common']?.['ariaLabelToggleDropdown'] ?? 'Toggle dropdown'; }

  private onChangeFn: (v: any) => void = () => { };
  private onTouchedFn: () => void = () => { };
  private completeTimer: ReturnType<typeof setTimeout> | null = null;

  // ── ControlValueAccessor ─────────────────────────────────────────────
  writeValue(value: any): void {
    this.value.set(value ?? null);
    this.inputText.set(this.labelOf(value));
  }
  registerOnChange(fn: (v: any) => void): void { this.onChangeFn = fn; }
  registerOnTouched(fn: () => void): void { this.onTouchedFn = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled.set(isDisabled); }

  // ── public API ───────────────────────────────────────────────────────
  focus(): void { this.inputRef.nativeElement.focus(); }
  hide(): void { this.clearCompleteTimer(); this.open.set(false); this.activeIndex.set(-1); }

  // ── rendering rows (items + optional group headers) ───────────────────
  get rows(): Array<{ group?: any; item?: any; index: number }> {
    const out: Array<{ group?: any; item?: any; index: number }> = [];
    let idx = 0;
    if (this.group()) {
      for (const g of this.suggestions() ?? []) {
        out.push({ group: g, index: -1 });
        for (const it of g?.items ?? []) out.push({ item: it, index: idx++ });
      }
    } else {
      for (const it of this.suggestions() ?? []) out.push({ item: it, index: idx++ });
    }
    return out;
  }
  get flatItems(): any[] {
    const suggestions = this.suggestions() ?? [];
    return this.group() ? suggestions.flatMap(g => g?.items ?? []) : suggestions;
  }
  /** Grouped rows for the `role=group` listbox structure; each item carries its flat (keyboard) index. */
  get displayGroups(): Array<{ group: any; labelId: string; items: Array<{ item: any; index: number }> }> {
    let idx = 0;
    return (this.suggestions() ?? []).map((g, gi) => ({
      group: g,
      labelId: `${this.listboxId}-group-${gi}`,
      items: (g?.items ?? []).map((item: any) => ({ item, index: idx++ }))
    }));
  }

  labelOf(option: any): string {
    if (option === null || option === undefined) return '';
    const optionLabel = this.optionLabel();
    if (optionLabel && typeof option === 'object') return option[optionLabel] ?? '';
    return String(option);
  }
  /** Marks the option matching the current model value (by label) as selected in the list. */
  isSelected(item: any): boolean {
    const v = this.value();
    return v !== null && v !== undefined && this.labelOf(item) === this.labelOf(v);
  }

  // ── input / dropdown ─────────────────────────────────────────────────
  onInput(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.inputText.set(query);
    if (query.length >= this.minQueryLength()) {
      this.open.set(true);
      this.scheduleComplete(query, event);
    } else {
      // Emptying the field clears the model too, otherwise the bound control keeps
      // the previously selected object while the input shows blank (stale submit).
      // Marking touched here keeps CVA state consistent with the change we emit.
      if (query.length === 0 && this.value() !== null) {
        this.value.set(null);
        this.onChangeFn(null);
        this.onTouchedFn();
      }
      this.hide();
    }
  }

  /** Debounces `completeMethod` by `delay` ms so typing does not query the server on every key. */
  private scheduleComplete(query: string, event: Event): void {
    this.clearCompleteTimer();
    const delay = this.delay();
    if (delay > 0) {
      this.completeTimer = setTimeout(() => {
        this.completeTimer = null;
        this.completeMethod.emit({ query, originalEvent: event });
      }, delay);
    } else {
      this.completeMethod.emit({ query, originalEvent: event });
    }
  }

  private clearCompleteTimer(): void {
    if (this.completeTimer !== null) {
      clearTimeout(this.completeTimer);
      this.completeTimer = null;
    }
  }

  ngOnDestroy(): void { this.clearCompleteTimer(); }

  /**
   * Opens the panel and asks the host for the unfiltered list.
   *
   * The query is deliberately empty, matching PrimeNG's default `dropdownMode="blank"`. It used to
   * send `inputText()`, which is the *selected value's label* - so clicking the arrow searched for
   * the thing already chosen ("All companies") and came back with just that. Client-side hosts hid
   * it by branching on `originalEvent.type === 'click'`; the one host that queries the server could
   * not, and its list came back empty.
   */
  onDropdownClick(event: MouseEvent): void {
    if (this.disabled()) return;
    if (this.open()) { this.hide(); return; }
    this.completeMethod.emit({ query: '', originalEvent: event });
    this.open.set(true);
    this.focus();
  }

  onInputFocus(event: FocusEvent): void { this.focused.emit(event); }

  onInputBlur(event: FocusEvent): void {
    this.hide();
    // Discard any typed-but-unselected text: the field always reflects the selected value's label.
    this.inputText.set(this.labelOf(this.value()));
    this.blurred.emit(event);
    this.onTouchedFn();
  }

  selectItem(item: any, event?: Event): void {
    this.value.set(item);
    this.inputText.set(this.labelOf(item));
    this.onChangeFn(item);
    this.selected.emit({ originalEvent: event ?? new Event('select'), value: item });
    this.hide();
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); if (this.open()) this.moveActive(1); break;
      case 'ArrowUp': event.preventDefault(); if (this.open()) this.moveActive(-1); break;
      case 'Enter': {
        const items = this.flatItems;
        if (this.open() && this.activeIndex() >= 0 && items[this.activeIndex()]) {
          event.preventDefault();
          this.selectItem(items[this.activeIndex()], event);
        }
        break;
      }
      case 'Escape': if (this.open()) { event.preventDefault(); this.hide(); } break;
    }
  }

  private moveActive(delta: number): void {
    const n = this.flatItems.length;
    if (!n) return;
    this.activeIndex.set((this.activeIndex() + delta + n) % n);
  }
}
